'use client';

import type { BlogCardProps } from '@routeroyal/theme-core';
import Link from 'next/link';
import Image from 'next/image';

export function RouteRoyalBlogCard({
  article,
  variant = 'default',
  showExcerpt = true,
  showCategory = true,
  showAuthor = true,
  locale = 'en',
  className = '',
}: BlogCardProps) {
  const isCompact = variant === 'compact';
  const isFeatured = variant === 'featured';

  return (
    <article className={`group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300 ${className}`}>
      <Link href={`/${locale}/news/${article.slug}`} className="block">
        {article.featuredImage && (
          <div className={`relative ${isFeatured ? 'h-80' : isCompact ? 'h-44' : 'h-56'} overflow-hidden`}>
            <Image
              src={article.featuredImage}
              alt={article.title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes={isFeatured ? '(max-width: 768px) 100vw, 800px' : '(max-width: 768px) 100vw, 400px'}
            />
            {showCategory && article.category && (
              <div className="absolute top-3 left-3">
                <span className="px-2.5 py-1 text-xs font-semibold text-white bg-blue-900/90 backdrop-blur-sm rounded-md">
                  {article.category}
                </span>
              </div>
            )}
          </div>
        )}
        <div className={`p-5 ${isFeatured ? 'lg:p-7' : ''}`}>
          <h3 className={`font-bold mb-2 line-clamp-2 text-gray-900 group-hover:text-blue-700 transition-colors ${
            isFeatured ? 'text-2xl' : isCompact ? 'text-base' : 'text-lg'
          }`}>
            {article.title}
          </h3>
          {showExcerpt && article.excerpt && !isCompact && (
            <p className="text-gray-600 mb-3 line-clamp-2 text-sm">{article.excerpt}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            {showAuthor && (
              <span className="font-medium text-gray-700">{article.author.name}</span>
            )}
            {showAuthor && <span>&middot;</span>}
            <time dateTime={article.publishedAt}>
              {new Date(article.publishedAt).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })}
            </time>
            {article.readTime && (
              <>
                <span>&middot;</span>
                <span>{article.readTime}</span>
              </>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}
